import { useCallback, useEffect, useRef, useState } from 'react';

// Formats the speech-to-text Lambda reads: Chrome and Firefox record webm,
// Safari mp4.
const VOICE_MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

const voiceMimeType = () =>
  typeof window !== 'undefined' && typeof window.MediaRecorder !== 'undefined'
    ? VOICE_MIME_TYPES.find(type => window.MediaRecorder.isTypeSupported(type))
    : undefined;

export const voiceSupported = () => !!navigator.mediaDevices?.getUserMedia && !!voiceMimeType();

interface Recording {
  recorder: MediaRecorder;
  stream: MediaStream;
  audio: AudioContext;
  chunks: Blob[];
  frame: number;
  timer: ReturnType<typeof setInterval>;
}

// Records a voice note: elapsed seconds and the input level (0–1) for the meter.
export const useVoiceRecorder = () => {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [level, setLevel] = useState(0);
  const current = useRef<Recording | null>(null);

  const release = useCallback(() => {
    const rec = current.current;
    if (!rec) {
      return;
    }
    current.current = null;
    cancelAnimationFrame(rec.frame);
    clearInterval(rec.timer);
    rec.stream.getTracks().forEach(track => track.stop());
    rec.audio.close().catch(() => {});
    setRecording(false);
    setSeconds(0);
    setLevel(0);
  }, []);

  // Throws when the microphone is unavailable or access is denied.
  const start = useCallback(async () => {
    if (current.current) {
      return;
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const recorder = new MediaRecorder(stream, { mimeType: voiceMimeType() });
    const audio = new AudioContext();
    const analyser = audio.createAnalyser();
    analyser.fftSize = 512;
    audio.createMediaStreamSource(stream).connect(analyser);
    const samples = new Uint8Array(analyser.fftSize);

    const rec: Recording = {
      recorder,
      stream,
      audio,
      chunks: [],
      frame: 0,
      timer: setInterval(() => setSeconds(value => value + 1), 1000),
    };
    const measure = () => {
      analyser.getByteTimeDomainData(samples);
      let sum = 0;
      samples.forEach(sample => {
        const centred = (sample - 128) / 128;
        sum += centred * centred;
      });
      setLevel(Math.min(1, Math.sqrt(sum / samples.length) * 4));
      rec.frame = requestAnimationFrame(measure);
    };
    recorder.ondataavailable = event => event.data.size && rec.chunks.push(event.data);
    recorder.start();
    rec.frame = requestAnimationFrame(measure);
    current.current = rec;
    setRecording(true);
  }, []);

  // Stops and returns the recording, or null if nothing was recorded.
  const stop = useCallback(
    () =>
      new Promise<Blob | null>(resolve => {
        const rec = current.current;
        if (!rec) {
          return resolve(null);
        }
        rec.recorder.onstop = () => {
          const blob = new Blob(rec.chunks, { type: rec.recorder.mimeType });
          release();
          resolve(blob.size ? blob : null);
        };
        rec.recorder.stop();
      }),
    [release]
  );

  const cancel = useCallback(() => {
    const rec = current.current;
    if (rec && rec.recorder.state !== 'inactive') {
      rec.recorder.onstop = null;
      rec.recorder.stop();
    }
    release();
  }, [release]);

  useEffect(() => cancel, [cancel]);

  return { recording, seconds, level, start, stop, cancel };
};
